import React, { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { postLink } from '../components/blogStore';
import { fetchPostBySlug, fetchPosts, incrementViews } from '../services/api';
import './BlogPostPage.css';

export default function BlogPostPage() {
  const { slug } = useParams();
  const [post, setPost]       = useState(null);
  const [related, setRelated] = useState([]);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    let cancelled = false;
    setPost(null);
    setNotFound(false);

    fetchPostBySlug(slug)
      .then(found => {
        if (cancelled) return;
        setPost(found);
        incrementViews(found.id);
        fetchPosts().then(all => {
          if (cancelled) return;
          setRelated(all.filter(p => p.tag === found.tag && p.id !== found.id).slice(0, 3));
        });

        // SEO meta tags — progressive enhancement for browsers that run JS.
        // Real crawlers (Google, WhatsApp, Facebook, Twitter) get correct
        // per-post meta tags server-side from backend/blog-meta.php instead,
        // so link previews and indexing don't depend on this running.
        document.title = `${found.title} | CropGuard AI`;
        upsertMeta('name', 'description', found.excerpt);
        upsertMeta('property', 'og:title', found.title);
        upsertMeta('property', 'og:description', found.excerpt);
        upsertMeta('property', 'og:url', `https://cropguards.com/blog/${slug}`);
        upsertMeta('property', 'og:type', 'article');
        if (found.featuredImg) upsertMeta('property', 'og:image', found.featuredImg);
      })
      .catch(() => { if (!cancelled) setNotFound(true); });

    return () => {
      cancelled = true;
      document.title = 'CropGuard AI — Crop Disease Detection';
    };
  }, [slug]);

  function upsertMeta(attrName, attrVal, content) {
    let el = document.querySelector(`meta[${attrName}="${attrVal}"]`);
    if (!el) { el = document.createElement('meta'); el.setAttribute(attrName, attrVal); document.head.appendChild(el); }
    el.setAttribute('content', content);
  }

  if (notFound) return (
    <div className="bp-notfound">
      <h1>404</h1>
      <p>Article not found.</p>
      <Link to="/blog" className="bp-back-btn">← Back to Blog</Link>
    </div>
  );

  if (!post) return (
    <div className="bp-loading">
      <div className="bp-spinner"></div>
      <p>Loading article...</p>
    </div>
  );

  const shareUrl = `https://cropguards.com/blog/${slug}`;

  return (
    <div className="bp-page">

      {/* Top Nav */}
      <nav className="bp-topbar">
        <div className="bp-topbar-inner">
          <Link to="/" className="bp-logo">🌿 CropGuard AI</Link>
          <div className="bp-breadcrumb">
            <Link to="/">Home</Link>
            <span>/</span>
            <Link to="/blog">Blog</Link>
            <span>/</span>
            <span>{post.tag}</span>
          </div>
          <Link to="/blog" className="bp-close-btn">← Back to Articles</Link>
        </div>
      </nav>

      {/* Hero */}
      {post.featuredImg && (
        <div className="bp-hero">
          <img src={post.featuredImg} alt={post.title} className="bp-hero-img" />
          <div className="bp-hero-overlay"></div>
        </div>
      )}

      {/* Article */}
      <main className="bp-main">
        <article className="bp-article">

          <div className="bp-badges">
            <span className="bp-tag">{post.tag}</span>
            {post.pinned && <span className="bp-featured-badge">📌 Featured</span>}
          </div>

          <h1 className="bp-title">{post.title}</h1>

          <div className="bp-meta-row">
            <div className="bp-author">
              <div className="bp-avatar">{post.authorAvatar || post.author?.charAt(0) || 'C'}</div>
              <div>
                <div className="bp-author-name">{post.author || 'CropGuard Team'}</div>
                <div className="bp-author-role">Agricultural Expert</div>
              </div>
            </div>
            <div className="bp-stats">
              <span>📅 {post.date}</span>
              <span>⏱ {post.readTime} read</span>
              {post.views !== undefined && <span>👁 {post.views.toLocaleString()} views</span>}
            </div>
          </div>

          {/* Share Bar */}
          <div className="bp-share-bar">
            <span className="bp-share-label">Share:</span>
            <button className="bp-share-btn" onClick={() => { navigator.clipboard.writeText(shareUrl); alert('Link copied!'); }}>
              🔗 Copy Link
            </button>
            <a className="bp-share-btn" href={`https://twitter.com/intent/tweet?text=${encodeURIComponent(post.title)}&url=${encodeURIComponent(shareUrl)}`} target="_blank" rel="noreferrer">
              𝕏 Tweet
            </a>
            <a className="bp-share-btn" href={`https://wa.me/?text=${encodeURIComponent(post.title + ' ' + shareUrl)}`} target="_blank" rel="noreferrer">
              💬 WhatsApp
            </a>
            <a className="bp-share-btn" href={`https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(shareUrl)}`} target="_blank" rel="noreferrer">
              👍 Facebook
            </a>
          </div>

          <div className="bp-divider"></div>
          <p className="bp-lead">{post.excerpt}</p>

          <div className="bp-body" dangerouslySetInnerHTML={{ __html: post.content }} />

          {post.tags?.length > 0 && (
            <div className="bp-tags-section">
              <span className="bp-tags-label">Tags:</span>
              {post.tags.map(t => <span key={t} className="bp-tag-small">#{t}</span>)}
            </div>
          )}

          <div className="bp-article-footer">
            <div className="bp-footer-brand">
              🌿 Published by <strong>CropGuard AI</strong> — Islamia University of Bahawalpur
            </div>
            <Link to="/blog" className="bp-back-btn">← Back to Articles</Link>
          </div>
        </article>

        {/* Related */}
        {related.length > 0 && (
          <aside className="bp-related">
            <h3 className="bp-related-title">Related Articles</h3>
            <div className="bp-related-grid">
              {related.map(r => (
                <Link key={r.id} to={postLink(r)} className="bp-related-card">
                  {r.cardImg
                    ? <img src={r.cardImg} alt={r.title} className="bp-related-img" />
                    : <div className="bp-related-fallback" style={{ background: r.bg }}>{r.emoji}</div>
                  }
                  <div className="bp-related-body">
                    <span className="bp-tag" style={{ fontSize: '.65rem' }}>{r.tag}</span>
                    <div className="bp-related-post-title">{r.title}</div>
                    <div className="bp-related-meta">{r.readTime} read</div>
                  </div>
                </Link>
              ))}
            </div>
          </aside>
        )}
      </main>
    </div>
  );
}
